// components/portal/useFlipRows.ts
//
// FLIP reordering for the table body. When a re-score changes the ranking, rows must be seen
// TRAVELLING to their new rank: without this they teleport and the user cannot tell what moved.
//
// FLIP = First / Last / Invert / Play. We keep the last known position of every row, and on the
// commit where the order changes we read the new positions, apply the inverse translation so the
// rows still LOOK where they were, then release the transform on the next frame so the browser
// animates them home.
//
// Two constraints drive the implementation:
//  - Only `transform` moves. Nothing here writes a layout property, so the animation stays on the
//    compositor even with a few hundred rows in the DOM.
//  - Reads and writes are strictly phased (measure every row first, THEN write every transform),
//    because interleaving them forces one synchronous reflow per row.
'use client';

import { useLayoutEffect, useRef } from 'react';

const FLIP_DURATION_MS = 420;
const FLIP_EASING = 'cubic-bezier(0.16, 0.84, 0.44, 1)';
/** Rows this far outside the viewport are re-positioned instantly: nobody is watching them. */
const OFFSCREEN_MARGIN_PX = 240;
/** A delta larger than this is not a re-rank, it is a re-mount or a page swap. Do not animate it. */
const MAX_PLAUSIBLE_DELTA_PX = 4000;

const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * @param containerRef  element wrapping the `[data-table-row-id]` rows.
 * @param orderSignature changes exactly when the rendered order (or the expanded row) changes.
 *                       The measure pass only runs then, so a plain data re-render costs nothing.
 */
export function useFlipRows(
  containerRef: React.RefObject<HTMLElement | null>,
  orderSignature: string
): void {
  // Row id -> offset from the top of the container. Container-relative on purpose: viewport
  // coordinates would move with the scroll and fabricate deltas out of a scroll gesture.
  const previousOffsetsRef = useRef<Map<string, number>>(new Map());
  const hasMeasuredRef = useRef(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const rows = container.querySelectorAll<HTMLElement>('[data-table-row-id]');
    if (rows.length === 0) {
      previousOffsetsRef.current = new Map();
      return;
    }

    const containerTop = container.getBoundingClientRect().top;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
    const nextOffsets = new Map<string, number>();
    const moved: Array<{ element: HTMLElement; delta: number }> = [];
    const previousOffsets = previousOffsetsRef.current;
    const shouldAnimate = hasMeasuredRef.current && !prefersReducedMotion();

    // --- Read phase: measure everything before touching a single style.
    rows.forEach((element) => {
      const rowId = element.getAttribute('data-table-row-id');
      if (!rowId) return;
      const rect = element.getBoundingClientRect();
      nextOffsets.set(rowId, rect.top - containerTop);
      if (!shouldAnimate) return;

      // A row that was not on the previous list is entering: it has no "before" position, so
      // it simply appears at its rank rather than sliding in from nowhere.
      const previousOffset = previousOffsets.get(rowId);
      if (previousOffset === undefined) return;

      const isOffscreen = rect.bottom < -OFFSCREEN_MARGIN_PX || rect.top > viewportHeight + OFFSCREEN_MARGIN_PX;
      if (isOffscreen) return;

      const delta = previousOffset - (rect.top - containerTop);
      if (Math.abs(delta) < 1 || Math.abs(delta) > MAX_PLAUSIBLE_DELTA_PX) return;
      moved.push({ element, delta });
    });

    previousOffsetsRef.current = nextOffsets;
    hasMeasuredRef.current = true;
    if (moved.length === 0) return;

    // --- Write phase: invert. The rows are now painted where they used to be.
    moved.forEach(({ element, delta }) => {
      element.style.transition = 'none';
      element.style.transform = `translate3d(0, ${delta}px, 0)`;
      element.style.willChange = 'transform';
    });

    // --- Play. Two frames: the first lets the inverted transform paint, the second releases it.
    let secondFrameId = 0;
    const firstFrameId = requestAnimationFrame(() => {
      secondFrameId = requestAnimationFrame(() => {
        moved.forEach(({ element }) => {
          element.style.transition = `transform ${FLIP_DURATION_MS}ms ${FLIP_EASING}`;
          element.style.transform = '';
        });
      });
    });

    const cleanupTimeoutId = setTimeout(() => {
      moved.forEach(({ element }) => {
        element.style.transition = '';
        element.style.transform = '';
        element.style.willChange = '';
      });
    }, FLIP_DURATION_MS + 80);

    return () => {
      cancelAnimationFrame(firstFrameId);
      if (secondFrameId) cancelAnimationFrame(secondFrameId);
      clearTimeout(cleanupTimeoutId);
      // A re-rank landing mid-flight must not inherit the previous transform, or the next
      // measure pass would read a translated position as the row's real one.
      moved.forEach(({ element }) => {
        element.style.transition = '';
        element.style.transform = '';
        element.style.willChange = '';
      });
    };
  }, [containerRef, orderSignature]);
}
